import { handleResponseOld, makeGet, makePost } from './helpers';
import { Catalog } from '@/types/Catalog';
import { CoverLots } from '@/types/CoverLot';
import { Seller } from '@/types/Seller';

/**
 * @category Item API
 * @see fetchCatalogsByIds
 */
type FetchCatalogsByIdsParams = {
    authToken: string;
    catalogIds: number[];
    deployment: string;
};

/**
 * Fetch catalog data for given catalogIds via Post to Item API service
 * @function fetchCatalogsByIds
 * @category Item API
 * @param {FetchCatalogsByIdsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/catalogs
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/react-site/routes/post-spa-catalogs.ts
 */
const fetchCatalogsByIds = ({ authToken, catalogIds, deployment }: FetchCatalogsByIdsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<ITEM-API>spa/small/catalogs' });
        request.send({ ids: catalogIds });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchCuratorsForCatalog
 */
type FetchCuratorsForCatalogParams = {
    catalogId: number;
    deployment: string;
};

/**
 * Fetch curators for given catalogId via Get to Item API service
 * @function fetchCuratorsForCatalog
 * @category Item API
 * @param {FetchCuratorsForCatalogParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/catalog/${catalogId}/curators
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/inventory/routes/get-catalog-curators.ts
 */
const fetchCuratorsForCatalog = ({ catalogId, deployment }: FetchCuratorsForCatalogParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({ deployment, path: `<ITEM-API>catalog/${catalogId}/curators` });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchRegisteredCatalogs
 */
type FetchRegisteredCatalogsParams = {
    authToken: string;
    deployment: string;
};

/**
 * Fetch catalog registration data for authed user via Get to Item API service
 * @function fetchRegisteredCatalogs
 * @category Item API
 * @param {FetchRegisteredCatalogsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/catalog/registration
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidder/routes/get-catalog-registration.ts
 */
const fetchRegisteredCatalogs = ({ authToken, deployment }: FetchRegisteredCatalogsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>catalog/registration' });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchUpcomingCatalogs
 */
type FetchUpcomingCatalogsParams = {
    authToken: string;
    deployment: string;
    forceUpdate: boolean;
    pageSize: number;
    today: boolean;
};

/**
 * @category Item API
 * @see fetchUpcomingCatalogs
 */
type FetchUpcomingCatalogsResponse = {
    data: {
        catalogs: Catalog[];
        sellers: Seller[];
        smallCoverLots: CoverLots[];
    };
    message: string;
    success: boolean;
};

/**
 * Fetch all upcoming catalogs via Get to Item API service
 * @function fetchUpcomingCatalogs
 * @category Item API
 * @param {FetchUpcomingCatalogsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/upcomingcatalogs
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/inventory/routes/get-upcoming-catalogs.ts
 */
const fetchUpcomingCatalogs = ({ authToken, deployment, forceUpdate, pageSize, today }: FetchUpcomingCatalogsParams) =>
    new Promise<FetchUpcomingCatalogsResponse>((resolve, reject) => {
        const request = makeGet({ authToken, deployment, path: '<ITEM-API>spa/small/upcomingcatalogs' });
        const query: any = {};
        if (pageSize) {
            query.pageSize = pageSize;
        }
        if (today) {
            query.today = true;
        }
        if (forceUpdate) {
            query.bustLocalCache = Date.now();
        }
        request.query(query);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postCatalogItems
 */
type PostCatalogItemsParams = {
    authToken: string;
    catalogId: number;
    count?: number | string;
    cursor: any;
    deployment: string;
    itemId?: number;
    page?: number;
};

/**
 * Sends index and page information and returns those particular items from a given catalogId via Post to Item API service
 * @function postCatalogItems
 * @category Item API
 * @param {PostCatalogItemsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/spa/small/catalog/items
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/inventory/routes/post-catalog-items.ts
 */
const postCatalogItems = ({ authToken, catalogId, count, cursor, deployment, itemId, page }: PostCatalogItemsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: '<ITEM-API>spa/small/catalog/items' });
        const data: any = {};

        if (cursor) {
            data.cursor = cursor;
            data.wrap = true;
        }

        if (page) {
            data.page = page;
        }

        if (count) {
            data.pageSize = count;
        }

        if (itemId) {
            data.itemId = itemId;
        }

        // @ts-ignore
        if (global.__SERVER__ && !cursor && cursor !== 0 && typeof page === 'number' && typeof count === 'number') {
            data.cursor = (page - 1) * count;
        }
        request.type('form');
        request.send({ catalogId, ...data });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Approval API
 * @see postRegisterForCatalog
 */
type PostRegisterForCatalogParams = {
    authToken: string;
    bidderId: number;
    catalogId: number;
    deployment: string;
    itemId: number;
};

/**
 * Registers given bidderId for given catalogId via Post to Approval API
 * @function postRegisterForCatalog
 * @category Approval API
 * @param {PostRegisterForCatalogParams}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/approval/catalog/${catalogId}/bidder/${bidderId}/register
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/ou2a17vn9h/resources/m5df41/methods/POST
 * @see https://github.com/LIVEauctioneers/approval/tree/master/functions/RegisterBidder
 */
const postRegisterForCatalog = ({ authToken, bidderId, catalogId, deployment, itemId }: PostRegisterForCatalogParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            apiPath: `catalog/${catalogId}/bidder/${bidderId}/register`,
            authToken,
            deployment,
            path: '<APPROVAL-API>',
        });
        request.send({
            acceptTerms: true,
            lotId: itemId,
        });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    fetchCatalogsByIds,
    fetchCuratorsForCatalog,
    fetchRegisteredCatalogs,
    fetchUpcomingCatalogs,
    postCatalogItems,
    postRegisterForCatalog,
};
